import { z } from "zod";

import { ERROR_CODES, type ErrorCode } from "./error-codes.js";

/**
 * The error envelope of section 04, 4.5.5.
 *
 * **Every non-2xx response has exactly this shape**, at every endpoint,
 * including validation errors and infrastructure failures. One shape is what
 * lets a frontend write the failure path once instead of per endpoint.
 *
 * It lives in `packages/contracts` beside the code registry because the six
 * frontends need it as much as the backend does, and because 9.1 puts the Zod
 * schemas here. The schema is the artefact the item's test runs against.
 *
 * The frontend branches on `code` and never on `message` (4.5.5). `message` is
 * the exact French sentence the interface displays when the error is a business
 * guard, stored with the guard rather than written in a screen, which is what
 * stops one rule being phrased two ways on two screens.
 */

/**
 * The `details` keys that 4.5.5 names, each beside the code that carries it.
 *
 * Declared rather than left as a free object so that a caller assembling one
 * gets the field name right, and so a frontend can rely on it. `details` stays
 * open, because a guard may carry its own payload (the section 04 example
 * carries `guard` and `missing`), but the named keys are typed.
 */
export const errorDetailsSchema = z
  .object({
    /**
     * Whether the caller may retry. Carried by the infrastructure failures,
     * `provider_unavailable` and `service_unavailable`, and taken from
     * `InfrastructureError.retryable` of 9.2.2, where the decision belongs to
     * whoever knows the resource rather than to whoever catches the error.
     */
    retryable: z.boolean().optional(),
    /** The version the caller should have sent. Carried by `conflict` (4.5.6). */
    currentVersion: z.number().int().nonnegative().optional(),
    /** Per-field messages. Carried by `invalid_request_body`. */
    fields: z.record(z.string(), z.string()).optional(),
    /** Attempts left before the lock. Carried by `pin_invalid`. */
    remaining: z.number().int().nonnegative().optional(),
    /** Open files that block an offboarding. Carried by `offboarding_blocked`. */
    openFiles: z.number().int().nonnegative().optional(),
    /** The gaps needing a justification. Carried by `derogation_required`. */
    rules: z.array(z.string()).optional(),
    /** Carried by `rate_limited`. */
    retryAfterSeconds: z.number().int().nonnegative().optional(),
    /** The external provider that failed. Carried by `provider_unavailable`. */
    provider: z.string().optional(),
  })
  .catchall(z.unknown());

export const errorEnvelopeSchema = z.object({
  error: z.object({
    /** From the closed registry. A code outside it is not a valid envelope. */
    code: z.enum(ERROR_CODES),
    message: z.string().min(1),
    details: errorDetailsSchema.optional(),
    /** Correlates with the structured log. */
    requestId: z.string().min(1),
    /** The W3C trace id, correlating with OpenTelemetry and Sentry. */
    traceId: z.string().min(1),
  }),
});

export type ErrorDetails = z.infer<typeof errorDetailsSchema>;
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

export function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return errorEnvelopeSchema.safeParse(value).success;
}

/** Throws on anything that is not an envelope. Used at the boundary and in tests. */
export function parseErrorEnvelope(value: unknown): ErrorEnvelope {
  return errorEnvelopeSchema.parse(value);
}

/** Assembles an envelope. The only constructor, so no endpoint invents a shape. */
export function errorEnvelope(input: {
  code: ErrorCode;
  message: string;
  requestId: string;
  traceId: string;
  details?: ErrorDetails;
}): ErrorEnvelope {
  const { code, message, requestId, traceId, details } = input;
  return {
    error: {
      code,
      message,
      requestId,
      traceId,
      // `exactOptionalPropertyTypes` is on, so an absent `details` is absent
      // rather than present and undefined, which would serialise as a null.
      ...(details === undefined ? {} : { details }),
    },
  };
}
